import { useContext, useEffect, useState } from 'react';
import AuthAxios from '../../axios';
import { FirebaseAnalyticsEvent, Login } from '../../types';
import { useNavigate } from 'react-router-dom';
import { AuthStateContext } from '../../contexts/AuthContext';
import { ModalContext } from '../../contexts/ModalContext';
import { getItem, LocalLanguage, setItem, USERINFO, USERTOKEN, RECENT_SOCIAL_LOGIN } from '../../utils/localStorage';
import FacebookLoginBtn from './FacebookLoginBtn';
import AppleLoginBtn from './AppleLoginBtn';
import GoogleLoginBtn from './GoogleLoginBtn';
import NaverLoginBtn from './NaverLoginBtn.jsx';
import KakaoLoginBtn from './KakaoLoginBtn';
import { logFirebaseEvent } from '../../utils/firebase/analytics';
import { SocialLoginPlatformEnum } from './type';
import { t } from 'i18next';

export default function SocialLoginButtons() {
  const lan = getItem(LocalLanguage);
  const navigate = useNavigate();
  const { setLoginSuccess } = useContext(AuthStateContext);
  const { handleModal } = useContext(ModalContext);
  const [ recentLogin, setRecentLogin ] = useState<SocialLoginPlatformEnum | undefined>(undefined);
  const [ tooltipStyle, setTooltipStyle ] = useState('0px');

  const postSignIn = async ({
    type,
    socialId,
    email,
    forceSignUp,
  }: {
    type: string;
    socialId: string;
    email: string;
    forceSignUp?: boolean;
  }) => {
    return AuthAxios.post<Login>('/auth/social', {
      type,
      socialId,
      email,
      forceSignUp,
      shouldGenerateNickname: true, // web 은 닉네임 자동 생성하고, 성별, 생년월일 입력 페이지 없음.
    })
      .then((resolve) => {
        const { user, accessToken, refreshToken } = resolve.data;

        //subscriptionInfo 나중에 로그인 후 구독권 만료에 대한 고지용 팝업 .
        setItem(USERINFO, user);
        setItem(USERTOKEN, { accessToken, refreshToken });
        setLoginSuccess(user);

        return user;
      })
      .then(async (user) => {
        if (user.termsAgreed) {
          logFirebaseEvent(FirebaseAnalyticsEvent.LOGIN, {
            user_uuid: user.id,
            login_platform: user.type,
          });
          let afterLoginRedirectPage = '/app/home'
          return navigate(afterLoginRedirectPage);
        }
        return navigate('/auth/agrement');
      })
      .catch((error) => {
        navigate('/auth', { replace: true });
        if (
          error.response.status === 409 &&
          (error.response.data.message as string).indexOf('email already exists') !== -1
        ) {
          const message = error.response.data.message;
          const { email, socials } = error.response.data.options;
          handleModal(
            <div className="w-[300] bg-N10 flex flex-col items-center rounded-lg text-N0 p-2 px-4">
              <p>{lan === 'en' ? message : '이미 가입된 이메일입니다.'}</p>
              <p>
                {lan === 'en'
                  ? 'To create a new social account with the same email, please click the button below.'
                  : '동일한 이메일로 다른 소셜 계정을 등록하려면 아래 버튼을 누르세요.'}
              </p>
              <br />
              <p>Email: {email}</p>
              <p>Social: {socials}</p>
              <div className="w-full h-20 flex flex-row place-content-center gap-6 text-N0 p-2 px-4">
                <button
                  className="flex-1 shrink-0 bg-N5 text-N10 rounded-lg text-base font-semibold"
                  onClick={() => handleModal()}
                >
                  {lan === 'en' ? 'Cancel' : '취소'}
                </button>
                <button
                  className="flex-1 shrink-0 bg-N5 text-N10 rounded-lg text-base font-semibold"
                  onClick={() => {
                    handleModal();
                    postSignIn({ type, socialId, email, forceSignUp: true });
                  }}
                >
                  {lan === 'en' ? 'Process Anyway' : '계속 진행하기'}
                </button>
              </div>
            </div>
          );
          return;
        }

        let message = error.response.data.message;
        if (error.response.data.message === '재가입은 탈퇴 3개월 이후부터 가능합니다.' && lan === 'en') {
          message = 'You can re-sign up 3 months after deleting your account.';
        }
        handleModal(
          <div className="w-[300] bg-N10 flex flex-col items-center rounded-[22px] text-N0 p-2 px-4">
            <div>{message ?? ''}</div>
            <button
              className="w-full bg-N5 text-N10 rounded-[22px] text-base font-semibold"
              onClick={() => handleModal()}
            >
              {lan === 'en' ? 'ok' : '확인'}
            </button>
          </div>
        );
      });
  };

  const afterSocialLoginSuccess = ({ type, socialId, email }: { type: SocialLoginPlatformEnum; socialId: string; email: string }) => {
    setRecentLogin(type);
    setItem(RECENT_SOCIAL_LOGIN, type);
    return postSignIn({ type, socialId, email });
  };

  // 컴포넌트 Mount시 처음 실행
  useEffect(() => {
    // 컴포넌트 마운트 시 최근 로그인 정보 불러오기
    const recentSocial = getItem(RECENT_SOCIAL_LOGIN);
    setRecentLogin(recentSocial);
  }, []);

  useEffect(() => {
    if (recentLogin) {
      const targetElement = document.querySelector(`[data-social="${recentLogin}"]`);
      if (targetElement) {
        // 부모 div 기준 상대 위치 계산
        const parentRect = (targetElement.closest('.social-buttons-container') as HTMLElement).getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();

        // 말풍선 중앙에 오도록 left 계산
        const left = (targetRect.left - parentRect.left) + (targetRect.width / 2) - 10;

        setTooltipStyle(`${left}px`);
      }
    }
  }, [recentLogin])
  return (
    <div className='flex flex-col w-full gap-4 md:gap-3 text-center'>
      <p className='md:hidden block text-white'>{t("login.start")}</p>
      <div className='flex flex-row md:flex-col gap-[10px] relative social-buttons-container'>
        <KakaoLoginBtn afterSocialLoginSuccess={afterSocialLoginSuccess} dataSocial={SocialLoginPlatformEnum.KAKAO} />
        <GoogleLoginBtn afterSocialLoginSuccess={afterSocialLoginSuccess} dataSocial={SocialLoginPlatformEnum.GOOGLE} />
        <NaverLoginBtn afterSocialLoginSuccess={afterSocialLoginSuccess} dataSocial={SocialLoginPlatformEnum.NAVER} />
        {/* <FacebookLoginBtn afterSocialLoginSuccess={afterSocialLoginSuccess} dataSocial={SocialLoginPlatformEnum.FACEBOOK} /> */}
        <AppleLoginBtn afterSocialLoginSuccess={afterSocialLoginSuccess} dataSocial={SocialLoginPlatformEnum.APPLE} />
      </div>
      <div className={`${recentLogin ? 'block' : 'hidden'} md:hidden relative pt-[15px]`}>
        {/* 최근 로그인 계정으로 말풍선 표시 */}
        <div className="bg-gray-700 text-white text-sm rounded-full px-4 py-2 w-full h-[36px]">
          {t("login.latest")}
          {/* 화살표 부분 */}
          <div 
            className={`absolute top-0 w-[0] h-[0] border-l-[10px] border-r-[10px] border-b-[22px] border-l-transparent border-r-transparent border-b-gray-700`}
            style={{ left: tooltipStyle }}
          ></div>
        </div>
      </div>
    </div>
  );
}
